// src/features/fourlink-config/components/blocks/Block2Steps/ConfiguracionFinal.jsx

import React, { useMemo } from 'react';

export const ConfiguracionFinal = ({
  selectedTipologias,
  centros,
  selectedEquipos,
  equiposConfig,
  selectedCategorias,
  selectedPlanes,
  selectedActivos,
  activosConfig,
  selectedTareas,
  tareasPorCategoriaDisponibles,
  tipologiaEquipoMatrix,
  tipologiaActivoMatrix,
  tareasAsignacionMatrix,
  onGenerateExcel,
}) => {
  // Calcular estadísticas
  const stats = useMemo(() => {
    const totalCombinacionesEquipos = Object.values(tipologiaEquipoMatrix).reduce(
      (total, qrs) => total + (Array.isArray(qrs) ? qrs.length : 0), 
      0
    );
    
    const totalCombinacionesActivos = Object.values(tipologiaActivoMatrix).filter(Boolean).length;
    
    const totalAsignacionesTareas = Object.values(tareasAsignacionMatrix).filter(Boolean).length;
    
    const qrsActivos = selectedEquipos.reduce((total, equipo) => 
      total + (equiposConfig[equipo]?.qrs?.filter(qr => qr.active).length || 0), 
      0
    );
    
    // Tareas sin asignar
    const tareasNoAsignadas = selectedTareas.filter(tareaId => {
      return !selectedTipologias.some(tipologia => {
        return selectedEquipos.some(equipo => {
          const selectedQRs = tipologiaEquipoMatrix[`${tipologia}-${equipo}`] || [];
          return selectedQRs.some(qrId => {
            return tareasAsignacionMatrix[`${tareaId}-${tipologia}-${equipo}-${qrId}`];
          });
        });
      });
    });
    
    // Activos sin asignar
    const activosNoAsignados = selectedActivos.filter(activo => {
      return !selectedTipologias.some(tipologia => {
        return tipologiaActivoMatrix[`${tipologia}-${activo}`];
      });
    });
    
    // Categorías de tareas sin seleccionar
    const categoriasVacias = Object.keys(tareasPorCategoriaDisponibles).filter(categoria => {
      const tareasDeCategoria = tareasPorCategoriaDisponibles[categoria];
      return !tareasDeCategoria.some(tarea => selectedTareas.includes(tarea.id));
    });
    
    return {
      totalCombinacionesEquipos,
      totalCombinacionesActivos,
      totalAsignacionesTareas,
      qrsActivos,
      tareasNoAsignadas,
      activosNoAsignados,
      categoriasVacias,
    };
  }, [
    selectedTipologias,
    selectedEquipos,
    selectedActivos,
    selectedTareas,
    equiposConfig,
    tipologiaEquipoMatrix,
    tipologiaActivoMatrix,
    tareasAsignacionMatrix,
    tareasPorCategoriaDisponibles,
  ]);
  
  // Obtener nombre de tarea por ID
  const getTareaNombre = (tareaId) => {
    let tareaEncontrada = null;
    Object.values(tareasPorCategoriaDisponibles).forEach(categoriaTareas => {
      const tarea = categoriaTareas.find(t => t.id === tareaId);
      if (tarea) tareaEncontrada = tarea;
    });
    return tareaEncontrada?.name || tareaId;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Configuración Final</h2>
      <p className="text-gray-600 mb-6">Revisa el resumen completo de la configuración:</p>
      
      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{selectedTipologias.length}</div>
          <div className="text-sm text-blue-800 font-medium">Tipologías</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-green-600">{centros.length}</div>
          <div className="text-sm text-green-800 font-medium">Centros</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-purple-600">{selectedEquipos.length}</div>
          <div className="text-sm text-purple-800 font-medium">Equipos</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-orange-600">{selectedTareas.length}</div>
          <div className="text-sm text-orange-800 font-medium">Tareas</div>
        </div>
      </div>

      {/* Resumen detallado */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Resumen Detallado</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tipologías y Centros */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3">Tipologías y Centros:</h4>
            <div className="space-y-2">
              {selectedTipologias.map(tipologia => {
                const centrosTipologia = centros.filter(c => c.tipologia === tipologia);
                return (
                  <div key={tipologia} className="bg-blue-50 p-3 rounded">
                    <div className="font-medium text-blue-800">{tipologia}</div>
                    <div className="text-sm text-blue-600">
                      {centrosTipologia.length} centro{centrosTipologia.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Configuración por Categorías */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3">Configuración por Categorías:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-1 border-b">
                <span>Equipos:</span>
                <span className="font-semibold">{selectedEquipos.length} seleccionados</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span>QRs activos:</span>
                <span className="font-semibold">{stats.qrsActivos}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span>Categorías Profesionales:</span>
                <span className="font-semibold">{selectedCategorias.length}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span>Planes:</span>
                <span className="font-semibold">{selectedPlanes.length} definidos</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span>Activos:</span>
                <span className="font-semibold">{selectedActivos.length} configurados</span>
              </div>
              <div className="flex justify-between py-1">
                <span>Tareas:</span>
                <span className="font-semibold">{selectedTareas.length} seleccionadas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Matrices configuradas */}
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-semibold text-gray-700 mb-4">Matrices Configuradas:</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-indigo-600">{stats.totalCombinacionesEquipos}</div>
              <div className="text-sm text-indigo-800 font-medium">Tipologías y Equipos/QRs</div>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-teal-600">{stats.totalCombinacionesActivos}</div>
              <div className="text-sm text-teal-800 font-medium">Tipologías y Activos</div>
            </div>
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-pink-600">{stats.totalAsignacionesTareas}</div>
              <div className="text-sm text-pink-800 font-medium">Tareas Asignadas</div>
            </div>
          </div>
        </div>

        {/* Alertas de Configuración */}
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-semibold text-gray-700 mb-4">Alertas de Configuración:</h4>
          
          <div className="space-y-4">
            {/* Tareas sin asignar - ROJO */}
            {stats.tareasNoAsignadas.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Aviso: Tareas sin asignar: {stats.tareasNoAsignadas.length} de {selectedTareas.length}
                </h5>
                <div className="text-sm text-red-700">
                  <p className="mb-2">Las siguientes tareas no están asignadas a ninguna tipología/equipo/QR:</p>
                  <div className="bg-red-100 p-3 rounded max-h-32 overflow-y-auto">
                    {stats.tareasNoAsignadas.map(tareaId => (
                      <div key={tareaId} className="text-xs text-red-800 mb-1">
                        - {getTareaNombre(tareaId)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Activos sin asignar - ROJO */}
            {stats.activosNoAsignados.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Aviso: Activos sin asignar: {stats.activosNoAsignados.length} de {selectedActivos.length}
                </h5>
                <div className="text-sm text-red-700">
                  <p className="mb-2">Los siguientes activos no están asignados a ninguna tipología:</p>
                  <div className="bg-red-100 p-3 rounded">
                    {stats.activosNoAsignados.map(activo => (
                      <div key={activo} className="text-xs text-red-800 mb-1">
                        - {activo}
                        {activosConfig[activo]?.unidad && (
                          <span className="text-red-600 ml-2">
                            ({activosConfig[activo].unidad}
                            {activosConfig[activo]?.min && activosConfig[activo]?.max && 
                              `, ${activosConfig[activo].min}-${activosConfig[activo].max}`
                            })
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Categorías de tareas sin seleccionar - NARANJA */}
            {stats.categoriasVacias.length > 0 && (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h5 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Categorías sin tareas seleccionadas: {stats.categoriasVacias.length} de {Object.keys(tareasPorCategoriaDisponibles).length}
                </h5>
                <div className="text-sm text-orange-700">
                  <p className="mb-2">Las siguientes categorías no tienen ninguna tarea o registro seleccionado:</p>
                  <div className="bg-orange-100 p-3 rounded">
                    {stats.categoriasVacias.map(categoria => (
                      <div key={categoria} className="text-xs text-orange-800 mb-1">
                        - {categoria} ({tareasPorCategoriaDisponibles[categoria].length} tareas/registros disponibles)
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-orange-600 mt-2 italic">
                    Considera si estas categorías son relevantes para el cliente
                  </p>
                </div>
              </div>
            )}

            {/* Mensaje cuando no hay alertas - VERDE */}
            {stats.tareasNoAsignadas.length === 0 && 
             stats.activosNoAsignados.length === 0 && 
             stats.categoriasVacias.length === 0 && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h5 className="font-semibold text-green-800 mb-1 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Sin alertas de configuración
                </h5>
                <p className="text-sm text-green-700">
                  Todas las tareas y activos seleccionados están correctamente asignados, y se han seleccionado elementos de todas las categorías disponibles.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Distribución de Activos por Tipología */}
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-semibold text-gray-700 mb-4">Distribución de Activos por Tipología:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedTipologias.map(tipologia => {
              const activosAsignados = selectedActivos.filter(activo => tipologiaActivoMatrix[`${tipologia}-${activo}`]);
              const porcentajeActivos = selectedActivos.length > 0 
                ? Math.round((activosAsignados.length / selectedActivos.length) * 100) 
                : 0;
              
              return (
                <div key={tipologia} className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h5 className="font-semibold text-indigo-800 mb-2">{tipologia}</h5>
                  <div className="text-2xl font-bold text-indigo-600">{porcentajeActivos}%</div>
                  <div className="text-sm text-indigo-700 mb-2">
                    {activosAsignados.length} de {selectedActivos.length} activos
                  </div>
                  <div className="mt-2 bg-indigo-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${porcentajeActivos}%` }}
                    />
                  </div>
                  
                  {activosAsignados.length > 0 && (
                    <div className="mt-3 text-xs text-indigo-700">
                      <strong>Activos:</strong> {activosAsignados.slice(0, 3).join(', ')}
                      {activosAsignados.length > 3 && ` +${activosAsignados.length - 3} más`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Botón de generación */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6 mt-6">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Configuración Lista</h3>
            <p className="text-gray-600 mb-6">
              La configuración está completa y lista para ser exportada e implementada en 4Link
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={onGenerateExcel}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                📊 Generar Archivo de Configuración
              </button>
            </div>
            
            <div className="mt-6 text-sm text-gray-500">
              <p>El archivo Excel contendrá 10+ pestañas con toda la configuración:</p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                <span className="bg-white px-2 py-1 rounded text-xs">Tipologías</span>
                <span className="bg-white px-2 py-1 rounded text-xs">Centros</span>
                <span className="bg-white px-2 py-1 rounded text-xs">Equipos/QRs</span>
                <span className="bg-white px-2 py-1 rounded text-xs">Categorías</span>
                <span className="bg-white px-2 py-1 rounded text-xs">Planes</span>
                <span className="bg-white px-2 py-1 rounded text-xs">Activos</span>
                <span className="bg-white px-2 py-1 rounded text-xs">Tareas</span>
                <span className="bg-white px-2 py-1 rounded text-xs">Matrices</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};